import {
  ActionIcon,
  Anchor,
  Breadcrumbs,
  Button,
  Group,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { IconFolder, IconMusic, IconPlayerPause, IconPlayerPlay } from '@tabler/icons-react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useAlbum, useAlbumTracks, useFolderContents, useLibraryFolders } from '~/api/queries'
import type { DirectoryEntry } from '~/api/types'
import { CoverArt } from '~/components/blocks/CoverArt/CoverArt'
import { EnqueueButton, EnqueueMenu } from '~/components/blocks/EnqueueButton'
import { FavoriteButton } from '~/components/blocks/FavoriteButton/FavoriteButton'
import { TrackTable } from '~/components/blocks/TrackTable/TrackTable'
import type { QueueItem } from '~/components/player/types'
import { queueItemUid, trackToQueueItem } from '~/components/player/utils'
import { usePlayer } from '~/hooks/use-player'
import { formatDuration, formatDurationLong } from '~/lib/format'

const searchSchema = z.object({
  folderId: z.string().optional(),
  path: z.string().optional(),
})

export const Route = createFileRoute('/_app/folders/')({
  validateSearch: searchSchema,
  component: FoldersPage,
})

function entryToQueueItem(entry: DirectoryEntry): QueueItem {
  return {
    uid: queueItemUid(),
    id: Number(entry.id),
    title: entry.title,
    artist: entry.artist ?? '',
    artistId: entry.artistId ?? 0,
    album: '',
    albumId: 0,
    duration: entry.duration ?? 0,
    coverArt: entry.coverArt,
    replayGain: null,
    tempo: null,
    keyName: null,
    energy: null,
    mood: null,
  }
}

function FoldersPage() {
  const { folderId, path } = Route.useSearch()
  const navigate = useNavigate()
  const player = usePlayer()
  const { data: folders, isLoading: foldersLoading } = useLibraryFolders()

  const activeFolderId = folderId || (folders?.data[0] ? String(folders.data[0].id) : undefined)
  const { data: contents, isLoading: contentsLoading } = useFolderContents(
    activeFolderId ? Number(activeFolderId) : 0,
    path
  )

  const handleFolderChange = (id: string | null) => {
    if (id) navigate({ to: '/folders', search: { folderId: id } })
  }

  const navigateToPath = (newPath?: string) => {
    navigate({
      to: '/folders',
      search: { folderId: activeFolderId, path: newPath || undefined },
    })
  }

  const fileEntries = contents?.children.filter((e) => !e.isDir) ?? []
  const queueItems = fileEntries.map(entryToQueueItem)

  const currentTrack = player.state.queue[player.state.currentIndex]
  const isPlayingFromHere =
    !player.state.radioMode &&
    currentTrack &&
    fileEntries.some((e) => Number(e.id) === currentTrack.id) &&
    player.state.isPlaying

  const handlePlayAll = () => {
    if (isPlayingFromHere) {
      player.pause()
    } else if (queueItems.length > 0) {
      player.play(queueItems)
    }
  }

  const pathSegments = path ? path.split('/') : []

  const breadcrumbs = [
    <Anchor key="root" onClick={() => navigateToPath()} component="button" size="sm">
      {contents?.name || 'Root'}
    </Anchor>,
    ...pathSegments.map((seg, i) => {
      const segPath = pathSegments.slice(0, i + 1).join('/')
      const isLast = i === pathSegments.length - 1
      return isLast ? (
        <Text key={segPath} size="sm" fw={500}>
          {seg}
        </Text>
      ) : (
        <Anchor key={segPath} onClick={() => navigateToPath(segPath)} component="button" size="sm">
          {seg}
        </Anchor>
      )
    }),
  ]

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Folders</Title>
        {(folders?.data.length ?? 0) > 1 && (
          <Select
            data={folders?.data.map((f) => ({ value: String(f.id), label: f.name })) || []}
            value={activeFolderId}
            onChange={handleFolderChange}
            w={200}
            placeholder="Select folder"
          />
        )}
      </Group>

      {foldersLoading ? (
        <Skeleton h={300} />
      ) : !activeFolderId ? (
        <Paper withBorder p="xl">
          <Text c="dimmed" ta="center">
            No library folders configured. Add one in{' '}
            <Anchor renderRoot={(props) => <Link {...props} to="/library" />}>
              Library settings
            </Anchor>
            .
          </Text>
        </Paper>
      ) : (
        <>
          {pathSegments.length > 0 && <Breadcrumbs>{breadcrumbs}</Breadcrumbs>}

          {contentsLoading ? (
            <Skeleton h={300} />
          ) : contents?.albumId ? (
            <AlbumDirectoryView albumId={contents.albumId} />
          ) : contents?.children.length === 0 ? (
            <Paper withBorder p="xl">
              <Text c="dimmed" ta="center">
                This directory is empty.
              </Text>
            </Paper>
          ) : (
            <>
              {fileEntries.length > 0 && (
                <Group>
                  <Button
                    leftSection={
                      isPlayingFromHere ? (
                        <IconPlayerPause size={16} />
                      ) : (
                        <IconPlayerPlay size={16} />
                      )
                    }
                    onClick={handlePlayAll}
                  >
                    {isPlayingFromHere ? 'Pause' : 'Play All'}
                  </Button>
                  <EnqueueButton tracks={queueItems} />
                </Group>
              )}
              <Paper withBorder>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40} />
                      <Table.Th w={50} />
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Artist</Table.Th>
                      <Table.Th w={80}>Tracks</Table.Th>
                      <Table.Th w={100}>Duration</Table.Th>
                      <Table.Th w={50} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {contents?.children.map((entry) => (
                      <DirectoryRow
                        key={entry.id + entry.title}
                        entry={entry}
                        currentPath={path}
                        onNavigate={navigateToPath}
                        allFileEntries={fileEntries}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            </>
          )}
        </>
      )}
    </Stack>
  )
}

function DirectoryRow({
  entry,
  currentPath,
  onNavigate,
  allFileEntries,
}: {
  entry: DirectoryEntry
  currentPath?: string
  onNavigate: (path: string) => void
  allFileEntries: DirectoryEntry[]
}) {
  const nextPath = currentPath ? `${currentPath}/${entry.title}` : entry.title
  const player = usePlayer()
  const currentTrack = player.state.queue[player.state.currentIndex]

  if (entry.isDir) {
    return (
      <Table.Tr style={{ cursor: 'pointer' }} onClick={() => onNavigate(nextPath)}>
        <Table.Td>
          <IconFolder size={18} stroke={1.5} />
        </Table.Td>
        <Table.Td />
        <Table.Td>
          <Text size="sm" fw={500}>
            {entry.title}
          </Text>
        </Table.Td>
        <Table.Td>
          {entry.artist && entry.artistId ? (
            <Text
              size="sm"
              c="dimmed"
              td="none"
              renderRoot={(props) => (
                <Link
                  {...props}
                  to="/artists/$artistId"
                  params={{ artistId: String(entry.artistId) }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              )}
            >
              {entry.artist}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              {entry.artist}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm" c="dimmed">
            {entry.trackCount ?? ''}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" c="dimmed">
            {entry.duration ? formatDuration(entry.duration) : ''}
          </Text>
        </Table.Td>
        <Table.Td />
      </Table.Tr>
    )
  }

  const trackId = Number(entry.id)
  const isActive = currentTrack?.id === trackId
  const isActiveAndPlaying = isActive && player.state.isPlaying

  // Find index among file entries only (for play-from-here)
  const fileIndex = allFileEntries.findIndex((e) => e.id === entry.id)

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isActive) {
      player.togglePlay()
    } else {
      player.play(allFileEntries.map(entryToQueueItem), {
        startIndex: fileIndex >= 0 ? fileIndex : 0,
      })
    }
  }

  return (
    <Table.Tr>
      <Table.Td>
        <IconMusic size={18} stroke={1.5} />
      </Table.Td>
      <Table.Td>
        <ActionIcon variant="subtle" onClick={handlePlay} size="sm">
          {isActiveAndPlaying ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
        </ActionIcon>
      </Table.Td>
      <Table.Td>
        <Text
          size="sm"
          c={isActive ? 'var(--mantine-primary-color-filled)' : undefined}
          fw={isActive ? 600 : undefined}
          td="none"
          renderRoot={(props) => (
            <Link {...props} to="/tracks/$trackId" params={{ trackId: entry.id }} />
          )}
        >
          {entry.title}
        </Text>
      </Table.Td>
      <Table.Td>
        {entry.artist && entry.artistId ? (
          <Text
            size="sm"
            c="dimmed"
            td="none"
            renderRoot={(props) => (
              <Link
                {...props}
                to="/artists/$artistId"
                params={{ artistId: String(entry.artistId) }}
              />
            )}
          >
            {entry.artist}
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            {entry.artist}
          </Text>
        )}
      </Table.Td>
      <Table.Td />
      <Table.Td>
        <Text size="sm" c="dimmed">
          {entry.duration ? formatDuration(entry.duration) : ''}
        </Text>
      </Table.Td>
      <Table.Td>
        <EnqueueMenu tracks={[entryToQueueItem(entry)]} />
      </Table.Td>
    </Table.Tr>
  )
}

function AlbumDirectoryView({ albumId }: { albumId: number }) {
  const { data: album, isLoading } = useAlbum(albumId)
  const { data: tracksData } = useAlbumTracks(albumId, { limit: 500 })
  const player = usePlayer()

  if (isLoading) return <Skeleton h={400} />
  if (!album) return <Text>Album not found</Text>

  const tracks = tracksData?.data ?? []
  const currentTrack = player.state.queue[player.state.currentIndex]
  const isThisAlbumContext = !player.state.radioMode && currentTrack?.albumId === albumId
  const isPlayingThisAlbum = isThisAlbumContext && player.state.isPlaying

  const handlePlayAll = () => {
    if (isPlayingThisAlbum) {
      player.pause()
    } else if (isThisAlbumContext) {
      player.resume()
    } else if (tracks.length > 0) {
      player.play(tracks.map(trackToQueueItem))
    }
  }

  return (
    <Stack gap="lg">
      <Group align="flex-start" gap="xl">
        <div style={{ width: 250, flexShrink: 0 }}>
          <CoverArt type="albums" id={album.id} alt={album.name} size={500} w={250} h={250} />
        </div>
        <Stack gap="xs" style={{ flex: 1 }}>
          <Title order={1}>{album.name}</Title>
          <Text
            renderRoot={(props) => (
              <Link
                {...props}
                to="/artists/$artistId"
                params={{ artistId: String(album.artistId) }}
              />
            )}
            size="lg"
            c="dimmed"
            td="none"
          >
            {album.artist}
          </Text>
          <Group gap="xs">
            {album.year && <Text c="dimmed">{album.year}</Text>}
            <Text c="dimmed">&middot;</Text>
            <Text c="dimmed">{album.trackCount} tracks</Text>
            <Text c="dimmed">&middot;</Text>
            <Text c="dimmed">{formatDurationLong(album.duration)}</Text>
          </Group>
          <Group mt="sm">
            <Button
              leftSection={
                isPlayingThisAlbum ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />
              }
              onClick={handlePlayAll}
            >
              {isPlayingThisAlbum ? 'Pause' : 'Play All'}
            </Button>
            {tracks.length > 0 && <EnqueueButton tracks={tracks.map(trackToQueueItem)} />}
            <FavoriteButton type="album" id={album.id} favorited={album.favorited} />
          </Group>
        </Stack>
      </Group>

      {tracks.length > 0 && <TrackTable tracks={tracks} showAlbum={false} showNumber />}
    </Stack>
  )
}
